import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { motion, AnimatePresence } from 'framer-motion';
import {
  ArrowLeft,
  Download,
  Upload,
  FileSpreadsheet,
  CheckCircle2,
  AlertCircle,
  X,
  Info,
  AlertTriangle,
  Building2,
  Loader2,
  Save,
  RefreshCw
} from 'lucide-react';
import * as XLSX from 'xlsx';
import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';
import { useDropzone } from 'react-dropzone';
import { ProjectType as CheckpointProjectType } from '../../../types/checkpoint';
import { Asset } from '../../../types/asset';
import projectTypeService from '../../../services/projectTypeService';
import { assetService } from '../../../services/assetService';
import { amsCheckpointService } from '../../../services/amsCheckpointService';
import documentMasterService from '../../../services/documentMasterService';
import uomMasterService from '../../../services/uomMasterService';
import auditTypeService, { AuditType } from '../../../services/auditTypeService';
import Button from '../../../shared/components/ui/button/Button';
import PageBreadcrumb from '../../../shared/PageBreadCrumb';
import Select from '../../../shared/components/form/Select';

// Modern Clean Brand Colors
const BRAND_COLORS = {
  primary: "#339274",
  primaryHover: "#29795f",
  primaryLight: "#F8FCFA",
  primarySoft: "#EDF7F2",
  accent: "#A3CD39",
  dark: "#007F66",
  success: "#10B981",
  successLight: "#F0FDF4",
  error: "#EF4444",
  errorLight: "#FEF2F2",
  warning: "#F59E0B",
  warningLight: "#FFFBEB",
};

// 0-based column index → Excel column letter (supports beyond Z: 26→AA, ...).
const colLetter = (index0: number): string => {
  let n = index0;
  let letter = '';
  do {
    letter = String.fromCharCode(65 + (n % 26)) + letter;
    n = Math.floor(n / 26) - 1;
  } while (n >= 0);
  return letter;
};

// Number of fixed columns before the dynamic per-audit-type Yes/No columns.
const FIXED_COLUMN_COUNT = 12;

// Interface for parsed Excel data
interface ParsedAmsCheckpointData {
  checkpointLevel: string; // 'project' | 'asset'
  assetName: string;
  name: string;
  executionType: string;
  documentApplicable: string;
  documentTypes?: string;
  designedParameterAvailable: string;
  designedParameterCategory?: string;
  designedParameterUom?: string;
  actualParameter?: string;
  imagesEnabled: string;
  remarksEnabled: string;
  auditTypes: string;
  errors: string[];
  rowIndex: number;
}

interface ValidationResult {
  isValid: boolean;
  validCheckpoints: ParsedAmsCheckpointData[];
  invalidCheckpoints: ParsedAmsCheckpointData[];
  totalRows: number;
}

export default function AmsBulkUploadCheckpointsPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // ========== State Management ==========
  const [projectTypes, setProjectTypes] = useState<CheckpointProjectType[]>([]);
  const [assets, setAssets] = useState<Asset[]>([]);
  const [documentTypes, setDocumentTypes] = useState<Array<{ value: string; label: string }>>([]);
  const [activeAuditTypes, setActiveAuditTypes] = useState<AuditType[]>([]);
  const [selectedProjectType, setSelectedProjectType] = useState<string>('');
  const [isLoadingProjectTypes, setIsLoadingProjectTypes] = useState(true);
  const [isLoadingAssets, setIsLoadingAssets] = useState(false);

  // File upload state
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);
  const [isProcessingFile, setIsProcessingFile] = useState(false);
  const [isGeneratingTemplate, setIsGeneratingTemplate] = useState(false);
  const [parsedData, setParsedData] = useState<ParsedAmsCheckpointData[]>([]);
  const [validationResult, setValidationResult] = useState<ValidationResult | null>(null);

  // Submission state
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [apiError, setApiError] = useState<string | null>(null);
  const [creationResult, setCreationResult] = useState<{
    totalCreated: number;
    totalFailed: number;
    totalSubmitted: number;
    errors?: Array<{ name: string; error: string; assetName: string }>;
  } | null>(null);

  // ========== Effects ==========

  // Fetch project types on mount
  useEffect(() => {
    const fetchProjectTypes = async () => {
      try {
        setIsLoadingProjectTypes(true);
        const response = await projectTypeService.getAllProjectTypes();
        const activeProjectTypes = response.filter(pt => pt.status === 'active');

        const convertedProjectTypes: CheckpointProjectType[] = activeProjectTypes.map(pt => ({
          _id: pt._id,
          name: pt.name,
          description: pt.description,
          status: pt.status,
          sequence: pt.sequence,
          displayNumber: pt.displayNumber,
          createdAt: pt.createdAt || '',
          updatedAt: pt.updatedAt || '',
          createdBy: typeof pt.createdBy === 'string' ? undefined : pt.createdBy,
          updatedBy: typeof pt.updatedBy === 'string' ? undefined : pt.updatedBy,
        })) as unknown as CheckpointProjectType[];

        setProjectTypes(convertedProjectTypes);

        // Set initial project type from URL params if available
        const urlProjectTypeId = searchParams.get('projectTypeId');
        if (urlProjectTypeId && convertedProjectTypes.some(pt => pt._id === urlProjectTypeId)) {
          setSelectedProjectType(urlProjectTypeId);
        }
      } catch (error) {
        console.error('Failed to fetch project types:', error);
        toast.error('Failed to load project types');
      } finally {
        setIsLoadingProjectTypes(false);
      }
    };

    fetchProjectTypes();
  }, [searchParams]);

  // Fetch document types on mount
  useEffect(() => {
    const fetchDocumentTypes = async () => {
      try {
        const options = await documentMasterService.getDocumentMasterDropdownOptions();
        setDocumentTypes(options);
      } catch (error) {
        console.error('Failed to fetch document types:', error);
        toast.error('Failed to load document types');
      }
    };

    fetchDocumentTypes();
  }, []);

  // Fetch active audit types on mount
  useEffect(() => {
    const fetchActiveAuditTypes = async () => {
      try {
        const types = await auditTypeService.getAllActiveAuditTypes();
        setActiveAuditTypes(types);
      } catch (error) {
        console.error('Failed to fetch audit types:', error);
        toast.error('Failed to load audit types');
      }
    };

    fetchActiveAuditTypes();
  }, []);

  // Fetch assets when project type changes
  useEffect(() => {
    const fetchAssets = async () => {
      if (!selectedProjectType) {
        setAssets([]);
        return;
      }

      try {
        setIsLoadingAssets(true);
        const response = await assetService.getAssetsByProjectType(selectedProjectType, { limit: 100 });

        let assetsData: Asset[] = [];
        if (response && typeof response === 'object' && 'assets' in response) {
          assetsData = response.assets;
        } else if (Array.isArray(response)) {
          assetsData = response;
        } else if (response && typeof response === 'object' && 'data' in response) {
          const data = (response as any).data;
          if (Array.isArray(data)) {
            assetsData = data;
          } else if (data && Array.isArray(data.docs)) {
            assetsData = data.docs;
          }
        }

        const activeAssets = assetsData.filter(asset => asset.status === 'active');
        setAssets(activeAssets);

      } catch (error) {
        console.error('Failed to fetch assets:', error);
        toast.error('Failed to load assets');
        setAssets([]);
      } finally {
        setIsLoadingAssets(false);
      }
    };

    fetchAssets();
  }, [selectedProjectType]);

  // ========== Excel Template Generation ==========

  const generateExcelTemplate = useCallback(async () => {
    if (!selectedProjectType) {
      toast.error('Please select Project Type before downloading template');
      return;
    }

    try {
      setIsGeneratingTemplate(true);
      const selectedProjectTypeName = projectTypes.find(pt => pt._id === selectedProjectType)?.name || 'Unknown';

      // Get all assets for the selected project type
      const availableAssets = assets.filter(asset => asset.status === 'active');
      const assetNames = availableAssets.map(asset => asset.name);

      // Options Lists
      const executionTypeOptions = ['manual', 'visual', 'hearing'];
      const booleanOptions = ['Yes', 'No'];
      const documentTypeNames = documentTypes.map(dt => dt.label);
      const auditTypeNames = activeAuditTypes.map(at => at.name);

      // Fetch UOM Categories
      const uomCategories = await uomMasterService.getCategories();

      // Fetch all UOMs for all categories
      const categoryUomsMap = new Map<string, string[]>();

      for (const category of uomCategories) {
        try {
          const response = await uomMasterService.getUomsByCategory(category);
          const symbols = response.map(uom => uom.symbol);
          categoryUomsMap.set(category, symbols);
        } catch (error) {
          console.warn(`Failed to fetch UOMs for category ${category}:`, error);
          categoryUomsMap.set(category, []);
        }
      }

      // Create Workbook and Sheets
      const workbook = new ExcelJS.Workbook();
      const ws = workbook.addWorksheet('AMS Checkpoint Template');
      const wsMaster = workbook.addWorksheet('MasterData', { state: 'veryHidden' });

      // --- Populate Master Data Sheet (Hidden) ---
      // First 6 columns for basic data
      wsMaster.getCell('A1').value = 'Assets';
      wsMaster.getCell('B1').value = 'ExecutionType';
      wsMaster.getCell('C1').value = 'Boolean';
      wsMaster.getCell('D1').value = 'DocumentTypes';
      wsMaster.getCell('E1').value = 'UOMCategories';
      wsMaster.getCell('F1').value = 'AuditTypes';

      assetNames.forEach((name, index) => { wsMaster.getCell(`A${index + 2}`).value = name; });
      executionTypeOptions.forEach((opt, index) => { wsMaster.getCell(`B${index + 2}`).value = opt; });
      booleanOptions.forEach((opt, index) => { wsMaster.getCell(`C${index + 2}`).value = opt; });
      documentTypeNames.forEach((name, index) => { wsMaster.getCell(`D${index + 2}`).value = name; });
      uomCategories.forEach((cat, index) => { wsMaster.getCell(`E${index + 2}`).value = cat; });
      auditTypeNames.forEach((name, index) => { wsMaster.getCell(`F${index + 2}`).value = name; });

      // Starting from column G (index 6), add UOMs for each category
      let currentColumn = 7; // Column G (1-indexed: A=1, B=2, ..., G=7)
      const categoryColumnMap = new Map<string, string>();
      
      uomCategories.forEach((category) => {
        const uoms = categoryUomsMap.get(category) || [];
        const columnLetter = String.fromCharCode(64 + currentColumn); // Convert to letter (A=65, so 64+1=A)
        
        // Create a valid Excel name (remove spaces and special characters)
        const safeCategoryName = category.replace(/[^a-zA-Z0-9_]/g, '_');
        
        // Add header with category name
        wsMaster.getCell(`${columnLetter}1`).value = safeCategoryName;
        
        // Add UOMs for this category
        uoms.forEach((uom, index) => {
          wsMaster.getCell(`${columnLetter}${index + 2}`).value = uom;
        });
        
        // Store the column letter for this category
        categoryColumnMap.set(category, columnLetter);
        
        // Define named range for this category
        const rangeName = safeCategoryName;
        const rangeAddress = `MasterData!$${columnLetter}$2:$${columnLetter}$${Math.max(2, uoms.length + 1)}`;
        
        // Add named range to workbook
        workbook.definedNames.add(rangeAddress, rangeName);
        
        currentColumn++;
      });

      // Define Ranges for basic data
      const assetRange = `'MasterData'!$A$2:$A$${Math.max(2, assetNames.length + 1)}`;
      const execTypeRange = `'MasterData'!$B$2:$B$${Math.max(2, executionTypeOptions.length + 1)}`;
      const boolRange = `'MasterData'!$C$2:$C$${Math.max(2, booleanOptions.length + 1)}`;
      const docTypeRange = `'MasterData'!$D$2:$D$${Math.max(2, documentTypeNames.length + 1)}`;
      const uomCategoryRange = `'MasterData'!$E$2:$E$${Math.max(2, uomCategories.length + 1)}`;
      const auditTypeRange = `'MasterData'!$F$2:$F$${Math.max(2, auditTypeNames.length + 1)}`;

      // --- Populate Main Template Sheet ---

      // Headers — column A is Checkpoint Level (Project/Asset); Asset Name is only
      // required for Asset-level rows. Audit Types is one Yes/No column per audit type
      // (multi-select without typing commas) — at least one must be Yes.
      const headers = [
        'Checkpoint Level *',
        'Asset Name (Asset level only)',
        'Checkpoint Name *',
        'Execution Type *',
        'Document Applicable *',
        'Document Types',
        'Designed Parameter Available *',
        'Designed Parameter Category',
        'Designed Parameter UOM',
        'Actual Parameter',
        'Images Enabled *',
        'Remarks Enabled *',
        ...auditTypeNames, // one Yes/No column per audit type
      ];

      ws.getRow(1).values = headers;

      // Styling Headers
      const headerRow = ws.getRow(1);
      headerRow.font = { bold: true, color: { argb: 'FFFFFFFF' }, size: 11 };
      headerRow.fill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: 'FF339274' } // Brand Primary
      };
      headerRow.alignment = { vertical: 'middle', horizontal: 'center', wrapText: true };
      headerRow.height = 40;

      // Instructions Row
      const instructions = [
        'Select: Project or Asset',
        'Required for Asset level; leave blank for Project level',
        'Enter checkpoint name',
        'Select: manual/visual/hearing',
        'Select Yes/No',
        'Type comma-separated values (e.g., Manual, Certificate). Use dropdown to see options.',
        'Select Yes/No',
        'Required if Designed Parameter Available is Yes - Select Category first',
        'Required if Designed Parameter Available is Yes - Select UOM based on Category',
        'Required if Designed Parameter Available is Yes - Enter actual parameter',
        'Select Yes/No',
        'Select Yes/No',
        ...auditTypeNames.map(() => 'Yes/No (mark at least one audit type Yes)'),
      ];
      ws.getRow(2).values = instructions;
      const instructionRow = ws.getRow(2);
      instructionRow.font = { italic: true, color: { argb: 'FF666666' }, size: 9 };
      instructionRow.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFFFFBEB' } };
      instructionRow.alignment = { vertical: 'middle', horizontal: 'center', wrapText: true };
      instructionRow.height = 30;

      // Set Column Widths
      ws.columns = [
        { width: 20 }, // Checkpoint Level
        { width: 30 }, // Asset Name
        { width: 35 }, // Checkpoint Name
        { width: 20 }, // Execution Type
        { width: 25 }, // Document Applicable
        { width: 30 }, // Document Types
        { width: 30 }, // Designed Parameter Available
        { width: 30 }, // Designed Parameter Category
        { width: 25 }, // Designed Parameter UOM
        { width: 30 }, // Actual Parameter
        { width: 20 }, // Images Enabled
        { width: 20 }, // Remarks Enabled
        ...auditTypeNames.map(() => ({ width: 22 })), // one column per audit type
      ];

      // Add Data Validations to Rows 3-1000
      for (let i = 3; i <= 1000; i++) {
        // Checkpoint Level (A) - Project / Asset (inline list)
        ws.getCell(`A${i}`).dataValidation = {
          type: 'list',
          allowBlank: false,
          formulae: ['"Project,Asset"'],
          showErrorMessage: true,
          errorTitle: 'Invalid Level',
          error: 'Please select Project or Asset'
        };

        // Asset Name (B)
        ws.getCell(`B${i}`).dataValidation = {
          type: 'list',
          allowBlank: true,
          formulae: [assetRange],
          showErrorMessage: true,
          errorTitle: 'Invalid Asset',
          error: 'Please select a valid asset from the list (Asset level only)'
        };

        // Execution Type (D)
        ws.getCell(`D${i}`).dataValidation = {
          type: 'list',
          allowBlank: true,
          formulae: [execTypeRange],
          showErrorMessage: true,
          errorTitle: 'Invalid Execution Type',
          error: 'Please select: manual, visual, or hearing'
        };

        // Document Applicable (E)
        ws.getCell(`E${i}`).dataValidation = {
          type: 'list',
          allowBlank: true,
          formulae: [boolRange],
          showErrorMessage: true,
          errorTitle: 'Invalid Selection',
          error: 'Please select Yes or No'
        };

        // Document Types (F) - List dropdown for reference only
        ws.getCell(`F${i}`).dataValidation = {
          type: 'list',
          allowBlank: true,
          formulae: [docTypeRange],
          showErrorMessage: false, // Don't show error to allow comma-separated values
          showInputMessage: true,
          promptTitle: 'Document Types',
          prompt: 'Type comma-separated values (e.g., "Manual, Certificate, Warranty"). Click dropdown arrow to see available options.'
        };

        // Designed Parameter Available (G)
        ws.getCell(`G${i}`).dataValidation = {
          type: 'list',
          allowBlank: true,
          formulae: [boolRange],
          showErrorMessage: true,
          errorTitle: 'Invalid Selection',
          error: 'Please select Yes or No'
        };

        // Designed Parameter Category (H)
        ws.getCell(`H${i}`).dataValidation = {
          type: 'list',
          allowBlank: true,
          formulae: [uomCategoryRange],
          showErrorMessage: true,
          errorTitle: 'Invalid Category',
          error: 'Please select a valid UOM category from the list'
        };

        // Designed Parameter UOM (I) - Dynamic dropdown based on Category (H)
        // Using INDIRECT to reference named range based on category value
        // The formula SUBSTITUTE replaces spaces with underscores to match named range
        ws.getCell(`I${i}`).dataValidation = {
          type: 'list',
          allowBlank: true,
          formulae: [`INDIRECT(SUBSTITUTE(H${i}," ","_"))`],
          showErrorMessage: false,
          showInputMessage: true,
          promptTitle: 'Designed Parameter UOM',
          prompt: 'Select UOM symbol. This dropdown will show UOMs for the category selected in column H.'
        };

        // Images Enabled (K)
        ws.getCell(`K${i}`).dataValidation = {
          type: 'list',
          allowBlank: true,
          formulae: [boolRange],
          showErrorMessage: true,
          errorTitle: 'Invalid Selection',
          error: 'Select Yes or No'
        };

        // Remarks Enabled (L)
        ws.getCell(`L${i}`).dataValidation = {
          type: 'list',
          allowBlank: true,
          formulae: [boolRange],
          showErrorMessage: true,
          errorTitle: 'Invalid Selection',
          error: 'Select Yes or No'
        };

        // Audit Types — one Yes/No dropdown column per audit type (columns M, N, ...)
        auditTypeNames.forEach((_name, atIdx) => {
          const cell = `${colLetter(FIXED_COLUMN_COUNT + atIdx)}${i}`;
          ws.getCell(cell).dataValidation = {
            type: 'list',
            allowBlank: true,
            formulae: [boolRange],
            showErrorMessage: true,
            errorTitle: 'Invalid Selection',
            error: 'Select Yes or No'
          };
        });
      }

      // Add Sample Data — one Project-level row (no asset) and one Asset-level row.
      // Mark only the first audit type Yes as a sample; the rest No.
      const sampleAuditFlags = auditTypeNames.map((_n, idx) => (idx === 0 ? 'Yes' : 'No'));
      const projectSample = [
        'Project',
        '', // Asset Name - blank for project level
        'Site boundary earthing continuity',
        'visual',
        'No',
        '',
        'No',
        '',
        '',
        '',
        'Yes',
        'Yes',
        ...sampleAuditFlags,
      ];
      const assetSample = [
        'Asset',
        assetNames[0] || 'Select Asset',
        'Check Hydraulic Oil Level',
        'manual',
        'Yes',
        'Maintenance Log, Inspection Report',
        'No', // Designed Parameter Available - default to No
        '', // Designed Parameter Category - empty, user should select
        '', // Designed Parameter UOM - empty, will populate based on category
        '', // Actual Parameter - empty, user should enter value
        'Yes',
        'Yes',
        ...sampleAuditFlags,
      ];
      ws.getRow(3).values = projectSample;
      ws.getRow(3).font = { italic: true, color: { argb: 'FF888888' } };
      ws.getRow(4).values = assetSample;
      ws.getRow(4).font = { italic: true, color: { argb: 'FF888888' } };

      // Generate Filename
      const timestamp = new Date().toISOString().split('T')[0];
      const filename = `AMS_Checkpoint_Template_${selectedProjectTypeName.replace(/[^a-zA-Z0-9]/g, '_')}_${timestamp}.xlsx`;

      // Save File
      const buffer = await workbook.xlsx.writeBuffer();
      saveAs(new Blob([buffer]), filename);

      toast.success('📊 Template downloaded successfully!');
    } catch (error) {
      console.error('Template generation error:', error);
      toast.error('Failed to generate template');
    } finally {
      setIsGeneratingTemplate(false);
    }
  }, [selectedProjectType, projectTypes, assets, documentTypes, activeAuditTypes]);

  // ========== Excel File Parsing ==========

  const parseExcelFile = useCallback(async (file: File): Promise<ParsedAmsCheckpointData[]> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();

      reader.onload = (e) => {
        try {
          const data = new Uint8Array(e.target?.result as ArrayBuffer);
          const workbook = XLSX.read(data, { type: 'array' });

          if (!workbook.SheetNames || workbook.SheetNames.length === 0) {
            throw new Error('Excel file is empty or corrupted.');
          }

          const sheetName = workbook.SheetNames[0]; // Assume first sheet is the template
          const worksheet = workbook.Sheets[sheetName];

          if (!worksheet) throw new Error('Excel file contains no data.');

          // Read as rows of cells. Row 0 = headers, row 1 = instructions, row 2+ = data.
          // Audit types are one Yes/No column per type (from FIXED_COLUMN_COUNT onward),
          // so we map those header names → column index and derive the selected types.
          const rows: any[][] = XLSX.utils.sheet_to_json(worksheet, { header: 1, blankrows: false });

          if (!rows || rows.length < 3) {
            throw new Error('No data found in Excel file. Please add checkpoint data starting from row 3.');
          }

          const headerRow = rows[0] || [];
          const auditTypeColumns: Array<{ index: number; name: string }> = [];
          for (let c = FIXED_COLUMN_COUNT; c < headerRow.length; c++) {
            const label = headerRow[c] ? String(headerRow[c]).trim() : '';
            if (label) auditTypeColumns.push({ index: c, name: label });
          }

          const cell = (row: any[], idx: number) => (row[idx] !== undefined && row[idx] !== null ? String(row[idx]).trim() : '');
          const isYes = (v: string) => v.trim().toLowerCase() === 'yes';

          const parsedCheckpoints: ParsedAmsCheckpointData[] = [];

          for (let r = 2; r < rows.length; r++) {
            const row = rows[r] || [];
            const checkpointLevel = cell(row, 0).toLowerCase();
            const assetName = cell(row, 1);
            const name = cell(row, 2);
            const executionType = cell(row, 3).toLowerCase();

            // Skip completely empty rows
            if (!name && !assetName && !executionType && !checkpointLevel) continue;

            // Audit types = names of the Yes/No columns marked "Yes"
            const selectedAuditTypes = auditTypeColumns
              .filter((atc) => isYes(cell(row, atc.index)))
              .map((atc) => atc.name);

            parsedCheckpoints.push({
              checkpointLevel,
              assetName,
              name,
              executionType,
              documentApplicable: cell(row, 4),
              documentTypes: cell(row, 5),
              designedParameterAvailable: cell(row, 6),
              designedParameterCategory: cell(row, 7),
              designedParameterUom: cell(row, 8),
              actualParameter: cell(row, 9),
              imagesEnabled: cell(row, 10),
              remarksEnabled: cell(row, 11),
              auditTypes: selectedAuditTypes.join(', '),
              errors: [],
              rowIndex: r + 1,
            });
          }

          resolve(parsedCheckpoints);
        } catch (error: any) {
          reject(new Error(`Failed to parse Excel file: ${error.message}`));
        }
      };

      reader.onerror = () => reject(new Error('Failed to read file'));
      reader.readAsArrayBuffer(file);
    });
  }, []);

  // ========== Validation ==========

  const validateParsedData = useCallback(async (data: ParsedAmsCheckpointData[]): Promise<ValidationResult> => {
    const validCheckpoints: ParsedAmsCheckpointData[] = [];
    const invalidCheckpoints: ParsedAmsCheckpointData[] = [];

    // Track checkpoint names within the file for duplicate detection (case-insensitive)
    const checkpointNamesInFile = new Map<string, Set<string>>();

    data.forEach(cp => {
      const errors: string[] = [];

      // Checkpoint Level drives whether Asset Name is required.
      const level = (cp.checkpointLevel || '').toLowerCase();
      const isProjectLevel = level === 'project';
      if (!level) {
        errors.push('Checkpoint Level is required (Project or Asset)');
      } else if (level !== 'project' && level !== 'asset') {
        errors.push('Checkpoint Level must be Project or Asset');
      }

      // Required field validations
      if (!cp.name) errors.push('Checkpoint Name is required');
      if (!isProjectLevel && !cp.assetName) errors.push('Asset Name is required for Asset-level checkpoints');
      if (!cp.executionType) errors.push('Execution Type is required');
      if (!cp.documentApplicable) errors.push('Document Applicable is required');
      if (!cp.designedParameterAvailable) errors.push('Designed Parameter Available is required');
      if (!cp.imagesEnabled) errors.push('Images Enabled is required');
      if (!cp.remarksEnabled) errors.push('Remarks Enabled is required');
      if (!cp.auditTypes || cp.auditTypes.trim() === '') errors.push('At least one Audit Type must be marked Yes');

      // Validate execution type
      if (cp.executionType && !['manual', 'visual', 'hearing'].includes(cp.executionType)) {
        errors.push('Execution Type must be: manual, visual, or hearing');
      }

      // Validate boolean fields
      const booleanFields = [
        { field: cp.documentApplicable, name: 'Document Applicable' },
        { field: cp.designedParameterAvailable, name: 'Designed Parameter Available' },
        { field: cp.imagesEnabled, name: 'Images Enabled' },
        { field: cp.remarksEnabled, name: 'Remarks Enabled' },
      ];

      booleanFields.forEach(({ field, name }) => {
        if (field && !['Yes', 'No'].includes(field)) {
          errors.push(`${name} must be Yes or No`);
        }
      });

      // Conditional validations
      // If Document Applicable is Yes, Document Types is required
      if (cp.documentApplicable === 'Yes') {
        if (!cp.documentTypes || cp.documentTypes.trim() === '') {
          errors.push('Document Types is required when Document Applicable is Yes');
        } else {
          // Validate each document type against available options
          const validDocumentTypeNames = documentTypes.map(dt => dt.label.toLowerCase());
          const enteredTypes = cp.documentTypes.split(',').map(t => t.trim()).filter(t => t);
          
          const invalidTypes = enteredTypes.filter(type => 
            !validDocumentTypeNames.includes(type.toLowerCase())
          );
          
          if (invalidTypes.length > 0) {
            errors.push(`Invalid document type(s): ${invalidTypes.join(', ')}. Please select from available options.`);
          }
        }
      }

      // Validate Audit Types against active audit type names (comma-separated, case-insensitive)
      if (cp.auditTypes && cp.auditTypes.trim() !== '') {
        const validAuditTypeNames = activeAuditTypes.map(at => at.name.toLowerCase());
        const enteredAuditTypes = cp.auditTypes.split(',').map(t => t.trim()).filter(t => t);

        const invalidAuditTypes = enteredAuditTypes.filter(type =>
          !validAuditTypeNames.includes(type.toLowerCase())
        );

        if (invalidAuditTypes.length > 0) {
          errors.push(`Invalid audit type(s): ${invalidAuditTypes.join(', ')}. Please select from available options.`);
        }
      }

      // If Designed Parameter Available is Yes, category, UOM, and actual parameter are required
      if (cp.designedParameterAvailable === 'Yes') {
        if (!cp.designedParameterCategory || cp.designedParameterCategory.trim() === '') {
          errors.push('Designed Parameter Category is required when Designed Parameter Available is Yes');
        }
        if (!cp.designedParameterUom || cp.designedParameterUom.trim() === '') {
          errors.push('Designed Parameter UOM is required when Designed Parameter Available is Yes');
        }
        if (!cp.actualParameter || cp.actualParameter.trim() === '') {
          errors.push('Actual Parameter is required when Designed Parameter Available is Yes');
        }
      }

      // Validate asset exists (asset level only)
      if (!isProjectLevel && cp.assetName) {
        const assetExists = assets.some(a => a.name.trim() === cp.assetName.trim());
        if (!assetExists) {
          errors.push(`Asset "${cp.assetName}" not found in selected Project Type`);
        }
      }

      // Check for duplicates within the file (case-insensitive). Project-level rows are
      // keyed under a shared "__project__" bucket; asset rows under their asset name.
      if (cp.name && (isProjectLevel || cp.assetName)) {
        const bucket = isProjectLevel ? '__project__' : cp.assetName.trim();
        const checkpointName = cp.name.trim().toLowerCase();

        if (!checkpointNamesInFile.has(bucket)) {
          checkpointNamesInFile.set(bucket, new Set());
        }

        if (checkpointNamesInFile.get(bucket)!.has(checkpointName)) {
          errors.push(
            isProjectLevel
              ? `Duplicate project-level checkpoint "${cp.name}" in the file`
              : `Duplicate checkpoint "${cp.name}" found for asset "${bucket}" in the file`,
          );
        } else {
          checkpointNamesInFile.get(bucket)!.add(checkpointName);
        }
      }

      cp.errors = errors;

      if (errors.length === 0) {
        validCheckpoints.push(cp);
      } else {
        invalidCheckpoints.push(cp);
      }
    });

    // Check for duplicates in database
    if (validCheckpoints.length > 0 && selectedProjectType) {
      try {
        // Fetch existing AMS checkpoints for this project type (asset + project levels)
        const [assetResp, projectResp] = await Promise.all([
          amsCheckpointService.getAmsCheckpoints({
            'project-type-id': selectedProjectType,
            'checkpoint-type': 'asset',
            limit: 1000,
          }),
          amsCheckpointService.getAmsCheckpoints({
            'project-type-id': selectedProjectType,
            'checkpoint-type': 'project',
            limit: 1000,
          }),
        ]);

        const existingCheckpoints = assetResp.data || [];
        const existingProjectNames = new Set<string>(
          (projectResp.data || [])
            .map((c: any) => (c.name || '').trim().toLowerCase())
            .filter((n: string) => n),
        );

        // Create a map of existing checkpoints: assetName -> Set of checkpoint names (lowercase)
        const existingCheckpointMap = new Map<string, Set<string>>();

        existingCheckpoints.forEach((existing: any) => {
          let assetId: string | undefined;
          if (typeof existing.assetId === 'string') {
            assetId = existing.assetId;
          } else if (existing.assetId && typeof existing.assetId === 'object') {
            assetId = existing.assetId._id || existing.assetId.id;
          }

          if (assetId) {
            const asset = assets.find(a => {
              const aId = a.id || a._id;
              return aId === assetId || String(aId) === String(assetId);
            });

            if (asset) {
              const assetName = asset.name.trim();
              if (!existingCheckpointMap.has(assetName)) {
                existingCheckpointMap.set(assetName, new Set());
              }
              const checkpointName = (existing.name || '').trim().toLowerCase();
              if (checkpointName) {
                existingCheckpointMap.get(assetName)!.add(checkpointName);
              }
            }
          }
        });

        // Check each valid checkpoint against database (iterate a copy while splicing).
        validCheckpoints.slice().forEach(cp => {
          const checkpointName = cp.name.trim().toLowerCase();
          const isProjectLevel = (cp.checkpointLevel || '').toLowerCase() === 'project';
          let isDuplicate = false;

          if (isProjectLevel) {
            if (existingProjectNames.has(checkpointName)) {
              cp.errors.push(`Project-level checkpoint "${cp.name}" already exists in database`);
              isDuplicate = true;
            }
          } else {
            const assetName = cp.assetName.trim();
            if (existingCheckpointMap.get(assetName)?.has(checkpointName)) {
              cp.errors.push(`Checkpoint "${cp.name}" already exists for asset "${assetName}" in database`);
              isDuplicate = true;
            }
          }

          if (isDuplicate) {
            invalidCheckpoints.push(cp);
            const index = validCheckpoints.indexOf(cp);
            if (index > -1) validCheckpoints.splice(index, 1);
          }
        });
      } catch (error) {
        console.error('Error checking database duplicates:', error);
        toast.error('Failed to check for duplicates. Please verify manually before submitting.');
      }
    }

    return {
      isValid: invalidCheckpoints.length === 0,
      validCheckpoints,
      invalidCheckpoints,
      totalRows: data.length
    };
  }, [selectedProjectType, assets, documentTypes, activeAuditTypes]);

  const processUploadedFile = useCallback(async (file: File) => {
    if (!selectedProjectType) {
      toast.error('Please select Project Type first');
      return;
    }

    try {
      setIsProcessingFile(true);
      setApiError(null);

      const parsed = await parseExcelFile(file);
      setParsedData(parsed);

      const validation = await validateParsedData(parsed);
      setValidationResult(validation);

      if (validation.isValid) {
        toast.success(`File valid! Ready to create ${validation.validCheckpoints.length} checkpoints.`);
      } else {
        toast.warning(`Found ${validation.invalidCheckpoints.length} issues in file.`);
      }
    } catch (error: any) {
      console.error('File processing error:', error);
      setApiError(error.message);
      toast.error(error.message);
    } finally {
      setIsProcessingFile(false);
    }
  }, [selectedProjectType, parseExcelFile, validateParsedData]);

  // File validation function
  const validateFile = (file: File): string | null => {
    if (file.size > 5 * 1024 * 1024) return 'File size must be < 5MB';
    const allowedTypes = [
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      'application/vnd.ms-excel'
    ];
    if (!allowedTypes.includes(file.type)) return 'Only .xlsx/.xls files allowed';
    return null;
  };

  // Dropzone configuration
  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop: (acceptedFiles) => {
      if (acceptedFiles.length > 0) {
        const file = acceptedFiles[0];
        const error = validateFile(file);
        if (error) {
          toast.error(error);
          return;
        }
        setUploadedFile(file);
        processUploadedFile(file);
      }
    },
    accept: {
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
      'application/vnd.ms-excel': ['.xls']
    },
    maxFiles: 1,
    // Only a Project Type is required — Project-level rows don't need assets to exist.
    disabled: !selectedProjectType,
  });

  // ========== Event Handlers ==========

  const handleSubmit = async () => {
    if (!uploadedFile) {
      toast.error('Please upload a file first');
      return;
    }

    if (!selectedProjectType) {
      toast.error('Please select a project type');
      return;
    }

    try {
      setIsSubmitting(true);
      setApiError(null);

      // Call the backend API to process the Excel file (projectTypeId is used for
      // Project-level rows, which carry no asset).
      const response = await amsCheckpointService.bulkUploadAmsCheckpoints(uploadedFile, selectedProjectType);

      if (response.success) {
        const { created, failed, total, errors: apiErrors } = response.data;

        setCreationResult({
          totalCreated: created,
          totalFailed: failed,
          totalSubmitted: total,
          errors: apiErrors.map(err => ({
            name: err.name,
            error: err.error,
            assetName: err.assetName
          }))
        });

        setShowSuccess(true);
        
        if (created > 0) {
          toast.success(`Created ${created} AMS checkpoint${created !== 1 ? 's' : ''}!`);
        }

        if (failed > 0) {
          toast.warning(`${failed} checkpoint${failed !== 1 ? 's' : ''} failed. Check the error details.`);
        }

        // Clear the form after successful submission
        setValidationResult(null);
        setParsedData([]);
        setUploadedFile(null);
      } else {
        setApiError(response.message || 'Failed to process file');
        toast.error(response.message || 'Failed to process file');
      }
    } catch (error: any) {
      const errorMessage = error?.response?.data?.message || error.message || 'Failed to upload file';
      setApiError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleReset = () => {
    setUploadedFile(null);
    setParsedData([]);
    setValidationResult(null);
    setApiError(null);
  };

  const handleCancel = () => navigate('/masters/checkpoints');

  const downloadErrorFile = useCallback(async () => {
    if (!validationResult || validationResult.invalidCheckpoints.length === 0) {
      toast.error('No errors to download');
      return;
    }

    try {
      const selectedProjectTypeName = projectTypes.find(pt => pt._id === selectedProjectType)?.name || 'Unknown';
      const availableAssets = assets.filter(asset => asset.status === 'active');
      const assetNames = availableAssets.map(asset => asset.name);
      const executionTypeOptions = ['manual', 'visual', 'hearing'];
      const booleanOptions = ['Yes', 'No'];
      const documentTypeNames = documentTypes.map(dt => dt.label);
      const auditTypeNames = activeAuditTypes.map(at => at.name);

      // Fetch UOM Categories
      const uomCategories = await uomMasterService.getCategories();

      // Fetch all UOMs for all categories
      const categoryUomsMap = new Map<string, string[]>();

      for (const category of uomCategories) {
        try {
          const response = await uomMasterService.getUomsByCategory(category);
          const symbols = response.map(uom => uom.symbol);
          categoryUomsMap.set(category, symbols);
        } catch (error) {
          console.warn(`Failed to fetch UOMs for category ${category}:`, error);
          categoryUomsMap.set(category, []);
        }
      }

      // Create workbook using ExcelJS
      const workbook = new ExcelJS.Workbook();

      // ========== Hidden Master Data Sheet ==========
      const masterDataSheet = workbook.addWorksheet('MasterData', { state: 'veryHidden' });

      // First 6 columns for basic data
      masterDataSheet.getCell('A1').value = 'Assets';
      masterDataSheet.getCell('B1').value = 'ExecutionType';
      masterDataSheet.getCell('C1').value = 'Boolean';
      masterDataSheet.getCell('D1').value = 'DocumentTypes';
      masterDataSheet.getCell('E1').value = 'UOMCategories';
      masterDataSheet.getCell('F1').value = 'AuditTypes';

      assetNames.forEach((name, index) => { masterDataSheet.getCell(`A${index + 2}`).value = name; });
      executionTypeOptions.forEach((opt, index) => { masterDataSheet.getCell(`B${index + 2}`).value = opt; });
      booleanOptions.forEach((opt, index) => { masterDataSheet.getCell(`C${index + 2}`).value = opt; });
      documentTypeNames.forEach((name, index) => { masterDataSheet.getCell(`D${index + 2}`).value = name; });
      uomCategories.forEach((cat, index) => { masterDataSheet.getCell(`E${index + 2}`).value = cat; });
      auditTypeNames.forEach((name, index) => { masterDataSheet.getCell(`F${index + 2}`).value = name; });

      // Starting from column G (index 6), add UOMs for each category
      let currentColumn = 7; // Column G (1-indexed: A=1, B=2, ..., G=7)
      const categoryColumnMap = new Map<string, string>();
      
      uomCategories.forEach((category) => {
        const uoms = categoryUomsMap.get(category) || [];
        const columnLetter = String.fromCharCode(64 + currentColumn); // Convert to letter (A=65, so 64+1=A)
        
        // Create a valid Excel name (remove spaces and special characters)
        const safeCategoryName = category.replace(/[^a-zA-Z0-9_]/g, '_');
        
        // Add header with category name
        masterDataSheet.getCell(`${columnLetter}1`).value = safeCategoryName;
        
        // Add UOMs for this category
        uoms.forEach((uom, index) => {
          masterDataSheet.getCell(`${columnLetter}${index + 2}`).value = uom;
        });
        
        // Store the column letter for this category
        categoryColumnMap.set(category, columnLetter);
        
        // Define named range for this category
        const rangeName = safeCategoryName;
        const rangeAddress = `MasterData!$${columnLetter}$2:$${columnLetter}$${Math.max(2, uoms.length + 1)}`;
        
        // Add named range to workbook
        workbook.definedNames.add(rangeAddress, rangeName);
        
        currentColumn++;
      });

      // ========== Main Template Sheet ==========
      const ws = workbook.addWorksheet('AMS Checkpoint Template');

      // Define named ranges for data validation
      const assetRange = `MasterData!$A$2:$A$${assetNames.length + 1}`;
      const execTypeRange = `MasterData!$B$2:$B$${executionTypeOptions.length + 1}`;
      const boolRange = `MasterData!$C$2:$C$${booleanOptions.length + 1}`;
      const docTypeRange = `MasterData!$D$2:$D$${documentTypeNames.length + 1}`;
      const uomCategoryRange = `MasterData!$E$2:$E$${uomCategories.length + 1}`;
      const auditTypeRange = `MasterData!$F$2:$F$${auditTypeNames.length + 1}`;

      // Headers — audit types are one Yes/No column per type; Errors is the last column.
      const headers = [
        'Checkpoint Level *',
        'Asset Name (Asset level only)',
        'Checkpoint Name *',
        'Execution Type *',
        'Document Applicable *',
        'Document Types',
        'Designed Parameter Available *',
        'Designed Parameter Category',
        'Designed Parameter UOM',
        'Actual Parameter',
        'Images Enabled *',
        'Remarks Enabled *',
        ...auditTypeNames,
        'Errors'
      ];

      ws.getRow(1).values = headers;

      // Styling Headers
      const headerRow = ws.getRow(1);
      headerRow.font = { bold: true, color: { argb: 'FFFFFFFF' }, size: 11 };
      headerRow.fill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: 'FF339274' } // Brand Primary
      };
      headerRow.alignment = { vertical: 'middle', horizontal: 'center', wrapText: true };
      headerRow.height = 40;

      // Instructions Row
      const instructions = [
        'Select: Project or Asset',
        'Required for Asset level; leave blank for Project level',
        'Enter checkpoint name',
        'Select: manual/visual/hearing',
        'Select Yes/No',
        'Type comma-separated values (e.g., Manual, Certificate). Use dropdown to see options.',
        'Select Yes/No',
        'Required if Designed Parameter Available is Yes - Select Category first',
        'Required if Designed Parameter Available is Yes - Select UOM based on Category',
        'Required if Designed Parameter Available is Yes - Enter actual parameter',
        'Select Yes/No',
        'Select Yes/No',
        ...auditTypeNames.map(() => 'Yes/No (mark at least one audit type Yes)'),
        'Error messages - Fix these issues'
      ];
      ws.getRow(2).values = instructions;
      const instructionRow = ws.getRow(2);
      instructionRow.font = { italic: true, color: { argb: 'FF666666' }, size: 9 };
      instructionRow.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFFFFBEB' } };
      instructionRow.alignment = { vertical: 'middle', horizontal: 'center', wrapText: true };
      instructionRow.height = 30;

      // Set Column Widths
      ws.columns = [
        { width: 20 }, // Checkpoint Level
        { width: 30 }, // Asset Name
        { width: 35 }, // Checkpoint Name
        { width: 20 }, // Execution Type
        { width: 25 }, // Document Applicable
        { width: 30 }, // Document Types
        { width: 30 }, // Designed Parameter Available
        { width: 30 }, // Designed Parameter Category
        { width: 25 }, // Designed Parameter UOM
        { width: 30 }, // Actual Parameter
        { width: 20 }, // Images Enabled
        { width: 20 }, // Remarks Enabled
        ...auditTypeNames.map(() => ({ width: 22 })), // audit type Yes/No columns
        { width: 60 }, // Errors
      ];

      // Add invalid checkpoint data
      validationResult.invalidCheckpoints.forEach((cp, index) => {
        const rowIndex = index + 3; // Start from row 3
        // Re-expand the selected audit types back into per-column Yes/No flags.
        const selectedSet = new Set(
          (cp.auditTypes || '').split(',').map(s => s.trim().toLowerCase()).filter(Boolean),
        );
        const auditFlags = auditTypeNames.map(n => (selectedSet.has(n.toLowerCase()) ? 'Yes' : 'No'));
        ws.addRow([
          cp.checkpointLevel ? (cp.checkpointLevel.toLowerCase() === 'project' ? 'Project' : 'Asset') : '',
          cp.assetName || '',
          cp.name || '',
          cp.executionType || '',
          cp.documentApplicable || '',
          cp.documentTypes || '',
          cp.designedParameterAvailable || '',
          cp.designedParameterCategory || '',
          cp.designedParameterUom || '',
          cp.actualParameter || '',
          cp.imagesEnabled || '',
          cp.remarksEnabled || '',
          ...auditFlags,
          cp.errors.join('; ')
        ]);

        // Add Data Validations for this row
        // Checkpoint Level (A)
        ws.getCell(`A${rowIndex}`).dataValidation = {
          type: 'list',
          allowBlank: false,
          formulae: ['"Project,Asset"'],
          showErrorMessage: true,
          errorTitle: 'Invalid Level',
          error: 'Please select Project or Asset'
        };

        // Asset Name (B)
        ws.getCell(`B${rowIndex}`).dataValidation = {
          type: 'list',
          allowBlank: true,
          formulae: [assetRange],
          showErrorMessage: true,
          errorTitle: 'Invalid Asset',
          error: 'Please select a valid asset from the list (Asset level only)'
        };

        // Execution Type (D)
        ws.getCell(`D${rowIndex}`).dataValidation = {
          type: 'list',
          allowBlank: true,
          formulae: [execTypeRange],
          showErrorMessage: true,
          errorTitle: 'Invalid Execution Type',
          error: 'Please select: manual, visual, or hearing'
        };

        // Document Applicable (E)
        ws.getCell(`E${rowIndex}`).dataValidation = {
          type: 'list',
          allowBlank: true,
          formulae: [boolRange],
          showErrorMessage: true,
          errorTitle: 'Invalid Selection',
          error: 'Please select Yes or No'
        };

        // Document Types (F) - List dropdown for reference only
        ws.getCell(`F${rowIndex}`).dataValidation = {
          type: 'list',
          allowBlank: true,
          formulae: [docTypeRange],
          showErrorMessage: false, // Don't show error to allow comma-separated values
          showInputMessage: true,
          promptTitle: 'Document Types',
          prompt: 'Type comma-separated values (e.g., "Manual, Certificate, Warranty"). Click dropdown arrow to see available options.'
        };

        // Designed Parameter Available (G)
        ws.getCell(`G${rowIndex}`).dataValidation = {
          type: 'list',
          allowBlank: true,
          formulae: [boolRange],
          showErrorMessage: true,
          errorTitle: 'Invalid Selection',
          error: 'Please select Yes or No'
        };

        // Designed Parameter Category (H)
        ws.getCell(`H${rowIndex}`).dataValidation = {
          type: 'list',
          allowBlank: true,
          formulae: [uomCategoryRange],
          showErrorMessage: true,
          errorTitle: 'Invalid Category',
          error: 'Please select a valid UOM category from the list'
        };

        // Designed Parameter UOM (I) - Dynamic dropdown based on Category (H)
        // Using INDIRECT to reference named range based on category value
        // The formula SUBSTITUTE replaces spaces with underscores to match named range
        ws.getCell(`I${rowIndex}`).dataValidation = {
          type: 'list',
          allowBlank: true,
          formulae: [`INDIRECT(SUBSTITUTE(H${rowIndex}," ","_"))`],
          showErrorMessage: false,
          showInputMessage: true,
          promptTitle: 'Designed Parameter UOM',
          prompt: 'Select UOM symbol. This dropdown will show UOMs for the category selected in column H.'
        };

        // Images Enabled (K)
        ws.getCell(`K${rowIndex}`).dataValidation = {
          type: 'list',
          allowBlank: true,
          formulae: [boolRange],
          showErrorMessage: true,
          errorTitle: 'Invalid Selection',
          error: 'Select Yes or No'
        };

        // Remarks Enabled (L)
        ws.getCell(`L${rowIndex}`).dataValidation = {
          type: 'list',
          allowBlank: true,
          formulae: [boolRange],
          showErrorMessage: true,
          errorTitle: 'Invalid Selection',
          error: 'Select Yes or No'
        };

        // Audit Types — one Yes/No dropdown column per audit type (columns M, N, ...)
        auditTypeNames.forEach((_name, atIdx) => {
          const cell = `${colLetter(FIXED_COLUMN_COUNT + atIdx)}${rowIndex}`;
          ws.getCell(cell).dataValidation = {
            type: 'list',
            allowBlank: true,
            formulae: [boolRange],
            showErrorMessage: true,
            errorTitle: 'Invalid Selection',
            error: 'Select Yes or No'
          };
        });

        // Style error cell (after the audit-type columns)
        const errorColLetter = colLetter(FIXED_COLUMN_COUNT + auditTypeNames.length);
        const errorCell = ws.getCell(`${errorColLetter}${rowIndex}`);
        errorCell.fill = {
          type: 'pattern',
          pattern: 'solid',
          fgColor: { argb: 'FFFEF2F2' } // Light red background
        };
        errorCell.font = { color: { argb: 'FFEF4444' }, size: 9 }; // Red text
        errorCell.alignment = { vertical: 'top', horizontal: 'left', wrapText: true };
      });

      // Generate and download file
      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const fileName = `AMS_Checkpoints_Errors_${selectedProjectTypeName.replace(/\s+/g, '_')}_${new Date().toISOString().split('T')[0]}.xlsx`;
      saveAs(blob, fileName);

      toast.success('Error file downloaded successfully!');
    } catch (error: any) {
      console.error('Error generating error file:', error);
      toast.error('Failed to generate error file');
    }
  }, [validationResult, projectTypes, selectedProjectType, assets, documentTypes, activeAuditTypes]);

  // ========== Render ==========

  return (
    <>
      {/* Success Modal */}
      <AnimatePresence>
        {showSuccess && creationResult && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[100] p-4"
            onClick={() => setShowSuccess(false)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0, y: 20 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.95, opacity: 0, y: 20 }}
              transition={{ type: 'spring', duration: 0.5 }}
              className="bg-white rounded-2xl shadow-2xl max-w-lg w-full overflow-hidden"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Header */}
              <div className="bg-gradient-to-r from-green-500 to-green-600 px-6 py-5 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center">
                    <CheckCircle2 className="w-6 h-6 text-white" />
                  </div>
                  <h3 className="text-lg font-bold text-white">Success!</h3>
                </div>
                <button
                  onClick={() => setShowSuccess(false)}
                  className="text-white/80 hover:text-white transition-colors"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              {/* Body */}
              <div className="p-6 space-y-4">
                <p className="text-sm text-gray-600">
                  Your AMS checkpoints have been created successfully.
                </p>

                {/* Stats */}
                <div className="grid grid-cols-3 gap-3">
                  <div className="bg-blue-50 p-3 rounded-lg text-center border border-blue-100">
                    <div className="text-xs font-semibold text-blue-600 uppercase tracking-wide mb-1">Submitted</div>
                    <div className="text-xl font-bold text-blue-900">{creationResult.totalSubmitted}</div>
                  </div>
                  <div className="bg-green-50 p-3 rounded-lg text-center border border-green-100">
                    <div className="text-xs font-semibold text-green-600 uppercase tracking-wide mb-1">Created</div>
                    <div className="text-xl font-bold text-green-900">{creationResult.totalCreated}</div>
                  </div>
                  <div className={`p-3 rounded-lg text-center border ${creationResult.totalFailed > 0 ? 'bg-red-50 border-red-100' : 'bg-gray-50 border-gray-100'}`}>
                    <div className={`text-xs font-semibold uppercase tracking-wide mb-1 ${creationResult.totalFailed > 0 ? 'text-red-600' : 'text-gray-400'}`}>Failed</div>
                    <div className={`text-xl font-bold ${creationResult.totalFailed > 0 ? 'text-red-900' : 'text-gray-400'}`}>{creationResult.totalFailed}</div>
                  </div>
                </div>

                {/* Failed Items */}
                {creationResult.errors && creationResult.errors.length > 0 && (
                  <div className="bg-red-50 border border-red-200 rounded-lg p-4 max-h-48 overflow-y-auto">
                    <h4 className="text-xs font-bold text-red-900 mb-2 flex items-center gap-2">
                      <AlertTriangle className="w-3.5 h-3.5" />
                      Failed Items ({creationResult.errors.length})
                    </h4>
                    <div className="space-y-2">
                      {creationResult.errors.slice(0, 5).map((err, idx) => (
                        <div key={idx} className="text-xs text-red-600 border-b border-red-100 last:border-0 py-2 px-2">
                          <div className="font-semibold">{err.assetName} - {err.name}</div>
                          <div>{err.error}</div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

              </div>

              {/* Close Button */}
              <div className="mt-6 flex justify-center pb-6">
                <Button
                  onClick={() => setShowSuccess(false)}
                  className="px-8 py-2.5 bg-green-600 hover:bg-green-700 text-white font-bold rounded-lg shadow-sm transition-all"
                >
                  Close
                </Button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <div className="w-full px-4 py-4 mx-auto max-w-[1920px]">
        <PageBreadcrumb
          items={[
            { name: "Masters", path: "/masters" },
            { name: "Checkpoints", path: "/masters/checkpoints" },
            { name: "AMS Bulk Upload", path: "/masters/checkpoints/bulk-ams-upload" },
          ]}
        />

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden"
        >
          {/* Header */}
          <div className="px-6 py-4 border-b border-gray-200 bg-gray-50/50">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="w-10 h-10 rounded-lg flex items-center justify-center border bg-gradient-to-br from-green-500 to-green-600 border-green-500 shadow-sm">
                  <Upload className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h1 className="text-xl font-bold text-gray-900 tracking-tight">
                    Bulk Upload Checkpoints
                  </h1>
                  <p className="text-xs text-gray-500 font-medium mt-0.5">
                    Download template, fill data, and upload for bulk creation
                  </p>
                </div>
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={handleCancel}
                disabled={isSubmitting}
                className="font-medium h-9 px-4 text-xs border rounded-lg hover:bg-white hover:text-red-600 hover:border-red-200 transition-colors"
              >
                <ArrowLeft className="w-3.5 h-3.5 mr-2" />
                Back
              </Button>
            </div>
          </div>

          <div className="p-6 grid grid-cols-1 lg:grid-cols-12 gap-8">

            {/* Global API Error Alert */}
            {apiError && (
              <div className="lg:col-span-12">
                <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-xl flex items-start gap-3">
                  <AlertTriangle className="w-5 h-5 flex-shrink-0 mt-0.5" />
                  <div>
                    <h3 className="font-bold text-sm">Submission Error</h3>
                    <p className="text-sm mt-1">{apiError}</p>
                    <button
                      onClick={() => setApiError(null)}
                      className="text-xs font-semibold underline mt-2 hover:text-red-800"
                    >
                      Dismiss
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Left Column: Instructions & Template */}
            <div className="lg:col-span-4 space-y-6">

              {/* Step 1: Project Type */}
              <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-5 relative overflow-hidden group hover:border-green-200 transition-colors">
                <div className="absolute top-0 right-0 w-24 h-24 bg-gradient-to-bl from-green-50/50 to-transparent -z-0 rounded-bl-3xl" />
                <div className="flex items-center gap-3 mb-4 relative z-10">
                  <div className="w-7 h-7 rounded-lg bg-green-50 text-green-600 flex items-center justify-center text-xs font-bold border border-green-100">1</div>
                  <h3 className="font-bold text-gray-900 text-sm">Select Project Type</h3>
                </div>

                <div className="space-y-3 relative z-10">
                  <div className="relative">
                    <Building2 className="absolute left-3 top-2.5 w-4 h-4 text-gray-400 pointer-events-none z-10" />
                    <Select
                      options={projectTypes.map(pt => ({ value: pt._id, label: pt.name }))}
                      placeholder={isLoadingProjectTypes ? "Loading..." : "Select Project Type"}
                      isLoading={isLoadingProjectTypes}
                      value={selectedProjectType ? { value: selectedProjectType, label: projectTypes.find(pt => pt._id === selectedProjectType)?.name || '' } : undefined}
                      onChange={(option) => setSelectedProjectType(option?.value || '')}
                      className="pl-9 text-sm"
                      menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
                    />
                  </div>
                  <p className="text-[11px] text-gray-500 leading-relaxed">
                    Selecting a project type will load its assets into the Excel template dropdowns.
                  </p>
                </div>
              </div>

              {/* Step 2: Download Template */}
              <div className={`bg-white rounded-xl border border-gray-100 shadow-sm p-5 relative overflow-hidden group transition-all duration-300 ${!selectedProjectType ? 'opacity-60 pointer-events-none grayscale' : 'hover:border-blue-200'}`}>
                <div className="absolute top-0 right-0 w-24 h-24 bg-gradient-to-bl from-blue-50/50 to-transparent -z-0 rounded-bl-3xl" />
                <div className="flex items-center gap-3 mb-4 relative z-10">
                  <div className="w-7 h-7 rounded-lg bg-blue-50 text-blue-600 flex items-center justify-center text-xs font-bold border border-blue-100">2</div>
                  <h3 className="font-bold text-gray-900 text-sm">Download Template</h3>
                </div>

                <div className="space-y-4 relative z-10">
                  <div className="p-3 bg-blue-50/50 rounded-lg border border-blue-100 flex items-start gap-3">
                    <FileSpreadsheet className="w-8 h-8 text-blue-500 flex-shrink-0" />
                    <div>
                      <h4 className="text-xs font-bold text-blue-900">Smart Excel Template</h4>
                      <p className="text-[10px] text-blue-700 mt-1 leading-relaxed">
                        Contains dynamic dropdowns for Assets, Execution Types, and more based on your selection.
                      </p>
                    </div>
                  </div>

                  <Button
                    onClick={generateExcelTemplate}
                    disabled={!selectedProjectType || isGeneratingTemplate}
                    className="w-full h-10 text-xs font-bold bg-blue-600 hover:bg-blue-700 text-white shadow-sm shadow-blue-200"
                  >
                    {isGeneratingTemplate ? (
                      <>
                        <Loader2 className="w-3.5 h-3.5 animate-spin mr-2" />
                        Generating...
                      </>
                    ) : (
                      <>
                        <Download className="w-3.5 h-3.5 mr-2" />
                        Download Excel Template
                      </>
                    )}
                  </Button>
                </div>
              </div>

              {/* Instructions Panel */}
              <div className="bg-gray-50 rounded-xl border border-gray-200 p-5">
                <div className="flex items-center gap-2 mb-3">
                  <Info className="w-4 h-4 text-gray-500" />
                  <h4 className="text-xs font-bold text-gray-700 uppercase tracking-wider">Instructions</h4>
                </div>
                <ul className="space-y-2">
                  {[
                    'Select Asset from dropdown',
                    'Enter Checkpoint Name',
                    'Choose Execution Type (manual/visual/hearing)',
                    'Select Document Applicable (Yes/No)',
                    'Select Parameter & Audit Options'
                  ].map((item, i) => (
                    <li key={i} className="flex items-start gap-2 text-[11px] text-gray-600">
                      <div className="w-1 h-1 rounded-full bg-gray-400 mt-1.5 flex-shrink-0" />
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            {/* Right Column: Upload & Validation */}
            <div className="lg:col-span-8 space-y-6">

              {/* Step 3: Upload Area */}
              <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-6">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-7 h-7 rounded-lg bg-indigo-50 text-indigo-600 flex items-center justify-center text-xs font-bold border border-indigo-100">3</div>
                  <h3 className="font-bold text-gray-900 text-sm">Upload & Validate</h3>
                </div>

                <div
                  {...getRootProps()}
                  className={`relative border-2 border-dashed rounded-xl p-8 text-center transition-all duration-300 group ${!selectedProjectType
                    ? 'border-gray-100 bg-gray-50 cursor-not-allowed'
                    : isDragActive
                      ? 'border-green-400 bg-green-50 scale-[0.99]'
                      : uploadedFile
                        ? 'border-green-200 bg-green-50/30'
                        : 'border-gray-200 hover:border-green-400 hover:bg-gray-50 cursor-pointer'
                    }`}
                >
                  <input {...getInputProps()} />

                  {isProcessingFile ? (
                    <div className="py-8">
                      <Loader2 className="w-10 h-10 text-green-500 animate-spin mx-auto mb-3" />
                      <p className="text-sm font-bold text-gray-900">Processing File...</p>
                      <p className="text-xs text-gray-500">Validating data structure</p>
                    </div>
                  ) : uploadedFile ? (
                    <div className="py-4">
                      <div className="w-12 h-12 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-3">
                        <CheckCircle2 className="w-6 h-6 text-green-600" />
                      </div>
                      <p className="text-sm font-bold text-gray-900">{uploadedFile.name}</p>
                      <p className="text-xs text-gray-500 mb-4">{(uploadedFile.size / 1024).toFixed(1)} KB</p>
                      <div onClick={(e) => e.stopPropagation()}>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={handleReset}
                          className="text-xs h-8 text-red-600 border-red-200 hover:bg-red-50"
                        >
                          Remove File
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <div className="py-8">
                      <div className="w-12 h-12 rounded-full bg-gray-100 flex items-center justify-center mx-auto mb-3 group-hover:scale-110 transition-transform duration-300">
                        <Upload className="w-6 h-6 text-gray-400 group-hover:text-green-500" />
                      </div>
                      <p className="text-sm font-bold text-gray-900">
                        {selectedProjectType ? "Click to Upload Excel File" : "Select Project Type First"}
                      </p>
                      <p className="text-xs text-gray-500 mt-1">or drag and drop here</p>
                    </div>
                  )}
                </div>
              </div>

              {/* Validation Results */}
              <AnimatePresence>
                {validationResult && (
                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -10 }}
                    className="space-y-4"
                  >
                    {/* Stats Cards */}
                    <div className="grid grid-cols-3 gap-4">
                      <div className="bg-white p-4 rounded-xl border border-gray-100 shadow-sm flex flex-col items-center justify-center text-center">
                        <span className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-1">Total Rows</span>
                        <span className="text-2xl font-bold text-gray-900">{validationResult.totalRows}</span>
                      </div>
                      <div className="bg-green-50 p-4 rounded-xl border border-green-100 shadow-sm flex flex-col items-center justify-center text-center">
                        <span className="text-[10px] font-bold text-green-600 uppercase tracking-wider mb-1">Valid</span>
                        <span className="text-2xl font-bold text-green-700">{validationResult.validCheckpoints.length}</span>
                      </div>
                      <div className={`p-4 rounded-xl border shadow-sm flex flex-col items-center justify-center text-center ${validationResult.invalidCheckpoints.length > 0 ? 'bg-red-50 border-red-100' : 'bg-gray-50 border-gray-100'}`}>
                        <span className={`text-[10px] font-bold uppercase tracking-wider mb-1 ${validationResult.invalidCheckpoints.length > 0 ? 'text-red-600' : 'text-gray-400'}`}>Invalid</span>
                        <span className={`text-2xl font-bold ${validationResult.invalidCheckpoints.length > 0 ? 'text-red-700' : 'text-gray-400'}`}>{validationResult.invalidCheckpoints.length}</span>
                      </div>
                    </div>

                    {/* Invalid List */}
                    {validationResult.invalidCheckpoints.length > 0 && (
                      <div className="bg-red-50/50 rounded-xl border border-red-100 overflow-hidden">
                        <div className="px-4 py-3 border-b border-red-100 bg-red-50 flex items-center justify-between">
                          <h4 className="text-xs font-bold text-red-900 flex items-center gap-2">
                            <AlertTriangle className="w-3.5 h-3.5" />
                            Validation Errors ({validationResult.invalidCheckpoints.length})
                          </h4>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={downloadErrorFile}
                            className="h-7 px-3 text-xs font-bold text-red-700 border-red-300 hover:bg-red-100"
                          >
                            <Download className="w-3 h-3 mr-1.5" />
                            Download Errors (Template Format)
                          </Button>
                        </div>
                        <div className="max-h-60 overflow-y-auto p-2 space-y-2">
                          {validationResult.invalidCheckpoints.map((cp, idx) => (
                            <div key={idx} className="bg-white p-3 rounded-lg border border-red-100 text-xs shadow-sm">
                              <div className="flex justify-between items-start mb-1">
                                <span className="font-bold text-gray-800">Row {cp.rowIndex}: {cp.name || 'Unknown'}</span>
                                <span className="text-red-600 font-bold bg-red-50 px-1.5 py-0.5 rounded">{cp.errors.length} Errors</span>
                              </div>
                              <div className="text-gray-500 mb-2">{cp.assetName} • {cp.executionType}</div>
                              <ul className="space-y-1 pl-4 list-disc text-red-600">
                                {cp.errors.map((err, i) => <li key={i}>{err}</li>)}
                              </ul>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Valid List Preview */}
                    {validationResult.validCheckpoints.length > 0 && (
                      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden shadow-sm">
                        <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between bg-gray-50/50">
                          <h4 className="text-xs font-bold text-gray-900 flex items-center gap-2">
                            <CheckCircle2 className="w-3.5 h-3.5 text-green-500" />
                            Valid Checkpoints Preview
                          </h4>
                          <span className="text-[10px] text-gray-500 font-medium">Showing first 10 of {validationResult.validCheckpoints.length}</span>
                        </div>
                        <div className="overflow-x-auto">
                          <table className="w-full text-xs text-left">
                            <thead className="bg-gray-50 text-gray-500 font-semibold border-b border-gray-100">
                              <tr>
                                <th className="px-4 py-2">Checkpoint Name</th>
                                <th className="px-4 py-2">Asset</th>
                                <th className="px-4 py-2">Execution Type</th>
                                <th className="px-4 py-2">Document</th>
                                <th className="px-4 py-2">Images</th>
                                <th className="px-4 py-2">Audit Types</th>
                              </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-100">
                              {validationResult.validCheckpoints.slice(0, 10).map((cp, idx) => (
                                <tr key={idx} className="hover:bg-gray-50/50">
                                  <td className="px-4 py-2 font-medium text-gray-900 truncate max-w-[200px]">{cp.name}</td>
                                  <td className="px-4 py-2 text-gray-600">{cp.assetName}</td>
                                  <td className="px-4 py-2 text-gray-600">
                                    <span className="bg-blue-50 text-blue-700 px-1.5 py-0.5 rounded border border-blue-100">{cp.executionType}</span>
                                  </td>
                                  <td className="px-4 py-2 text-gray-600">{cp.documentApplicable}</td>
                                  <td className="px-4 py-2 text-gray-600">{cp.imagesEnabled}</td>
                                  <td className="px-4 py-2 text-gray-600 truncate max-w-[200px]">{cp.auditTypes}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    )}

                    {/* Submit Actions */}
                    <div className="flex justify-end gap-3 pt-2">
                      <Button
                        variant="outline"
                        onClick={handleReset}
                        disabled={isSubmitting}
                        className="h-10 px-6 text-xs font-bold"
                      >
                        Reset
                      </Button>
                      <Button
                        variant="sorigin"
                        onClick={handleSubmit}
                        disabled={isSubmitting || validationResult.validCheckpoints.length === 0}
                        className="h-10 px-6 text-xs font-bold min-w-[150px] bg-green-600 hover:bg-green-700 shadow-sm shadow-green-200"
                      >
                        {isSubmitting ? (
                          <>
                            <Loader2 className="w-3.5 h-3.5 animate-spin mr-2" />
                            Creating...
                          </>
                        ) : (
                          <>
                            <Save className="w-3.5 h-3.5 mr-2" />
                            Create {validationResult.validCheckpoints.length} Checkpoint{validationResult.validCheckpoints.length !== 1 ? 's' : ''}
                            {validationResult.invalidCheckpoints.length > 0 && ` (${validationResult.invalidCheckpoints.length} invalid skipped)`}
                          </>
                        )}
                      </Button>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </div>
        </motion.div>
      </div>
    </>
  );
}
